import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Shield, Ban, Eye, EyeOff, Filter } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

export const ChatModeration = () => {
  const [blockedWords, setBlockedWords] = useState<string[]>(['spam', 'scam']);
  const [newBlockedWord, setNewBlockedWord] = useState('');
  const [slowMode, setSlowMode] = useState(false);
  const [slowModeSeconds, setSlowModeSeconds] = useState(5);
  const [subscribersOnly, setSubscribersOnly] = useState(false);

  const addBlockedWord = () => {
    if (newBlockedWord && !blockedWords.includes(newBlockedWord.toLowerCase())) {
      setBlockedWords([...blockedWords, newBlockedWord.toLowerCase()]);
      setNewBlockedWord('');
    }
  };

  const removeBlockedWord = (word: string) => {
    setBlockedWords(blockedWords.filter(w => w !== word));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Chat Moderation
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Quick Settings */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Label htmlFor="slow-mode">Slow Mode</Label>
              <Badge variant="outline" className="text-xs">
                {slowModeSeconds}s delay
              </Badge>
            </div>
            <Switch
              id="slow-mode"
              checked={slowMode}
              onCheckedChange={setSlowMode}
            />
          </div>

          {slowMode && (
            <Input
              type="number"
              min="1"
              max="120"
              value={slowModeSeconds}
              onChange={(e) => setSlowModeSeconds(Number(e.target.value))}
              className="h-8"
            />
          )}

          <div className="flex items-center justify-between">
            <Label htmlFor="subscribers-only">Subscribers Only</Label>
            <Switch
              id="subscribers-only"
              checked={subscribersOnly}
              onCheckedChange={setSubscribersOnly}
            />
          </div>
        </div>

        {/* Blocked Words */}
        <div className="pt-4 border-t space-y-3">
          <Label className="flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Blocked Words
          </Label>
          <div className="flex gap-2">
            <Input
              placeholder="Add word to block"
              value={newBlockedWord}
              onChange={(e) => setNewBlockedWord(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addBlockedWord()}
            />
            <Button size="sm" onClick={addBlockedWord}>
              <Ban className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {blockedWords.map((word) => (
              <Badge
                key={word}
                variant="secondary"
                className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground"
                onClick={() => removeBlockedWord(word)}
              >
                {word} ×
              </Badge>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="pt-4 border-t space-y-2">
          <Label>Quick Actions</Label>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="flex-1">
              <Eye className="h-4 w-4 mr-2" />
              Clear Chat
            </Button>
            <Button variant="outline" size="sm" className="flex-1">
              <EyeOff className="h-4 w-4 mr-2" />
              Hide Chat
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};